import { Usuario } from '@/types';
import { UsuariosService } from '@/services';

let usuariosCache: Map<number, Usuario> = new Map();
let usuariosCachePromise: Promise<void> | null = null;

/**
 * Carrega todos os usuários e armazena em cache
 */
async function loadUsersCache(): Promise<void> {
  if (usuariosCachePromise) {
    return usuariosCachePromise;
  }

  usuariosCachePromise = (async () => {
    try {
      const usuarios = await UsuariosService.getUsuarios();
      usuariosCache.clear();
      usuarios.forEach(user => {
        usuariosCache.set(user.id, user);
      });
    } catch (error) {
      console.error('Erro ao carregar usuários para cache de menções:', error);
    }
  })();

  return usuariosCachePromise;
}

/**
 * Processa menções no HTML, substituindo @[id:123] por elementos estilizados
 */
export async function processMentions(html: string): Promise<string> {
  if (!html) return html;

  await loadUsersCache();

  const mentionRegex = /@\[id:(\d+)\]/g;
  
  let processedHtml = html;
  const mentions = Array.from(html.matchAll(mentionRegex));
  
  for (const mention of mentions) {
    const fullMatch = mention[0];
    const userId = parseInt(mention[1]);
    
    const user = usuariosCache.get(userId);
    const userName = user?.nome || `Usuário #${userId}`;
    
    // Criar elemento HTML estilizado para a menção
    // Badge com cor que contrasta bem com fundo azul (verde esmeralda)
    const mentionHtml = `<span class="mention-badge" data-user-id="${userId}" style="display: inline-flex; align-items: center; gap: 6px; padding: 5px 14px; margin: 0 3px; background: linear-gradient(135deg, #10B981 0%, #059669 100%); color: white; border-radius: 18px; font-size: 0.8125rem; font-weight: 500; cursor: pointer; transition: all 0.2s ease; vertical-align: middle; white-space: nowrap; box-shadow: 0 2px 6px rgba(16, 185, 129, 0.25); border: 1px solid rgba(255, 255, 255, 0.15);" onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 4px 12px rgba(16, 185, 129, 0.35)'; this.style.background='linear-gradient(135deg, #059669 0%, #047857 100%)';" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 6px rgba(16, 185, 129, 0.25)'; this.style.background='linear-gradient(135deg, #10B981 0%, #059669 100%)';" title="Mencionado: ${userName}">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0; opacity: 0.95;">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
      <span style="line-height: 1.3; letter-spacing: 0.01em;">${userName}</span>
    </span>`;
    
    // Substituir a menção pelo HTML estilizado
    processedHtml = processedHtml.replace(fullMatch, mentionHtml);
  }
  
  return processedHtml;
}

/**
 * Processa menções de forma síncrona (para uso em tempo real no editor)
 * Retorna uma Promise que resolve com o HTML processado
 */
export function processMentionsSync(html: string, users: Usuario[] = []): string {
  if (!html) return html;

  // Atualizar cache com usuários fornecidos
  users.forEach(user => {
    usuariosCache.set(user.id, user);
  });

  // Regex para encontrar menções no formato @[id:123]
  const mentionRegex = /@\[id:(\d+)\]/g;
  
  let processedHtml = html;
  const mentions = Array.from(html.matchAll(mentionRegex));
  
  // Processar cada menção encontrada
  for (const mention of mentions) {
    const fullMatch = mention[0]; // @[id:123]
    const userId = parseInt(mention[1]); // 123
    
    const user = usuariosCache.get(userId);
    const userName = user?.nome || `Usuário #${userId}`;
    
    // Criar elemento HTML estilizado para a menção
    // Badge com cor que contrasta bem com fundo azul (verde esmeralda)
    const mentionHtml = `<span class="mention-badge" data-user-id="${userId}" style="display: inline-flex; align-items: center; gap: 6px; padding: 5px 14px; margin: 0 3px; background: linear-gradient(135deg, #10B981 0%, #059669 100%); color: white; border-radius: 18px; font-size: 0.8125rem; font-weight: 500; cursor: pointer; transition: all 0.2s ease; vertical-align: middle; white-space: nowrap; box-shadow: 0 2px 6px rgba(16, 185, 129, 0.25); border: 1px solid rgba(255, 255, 255, 0.15);" onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 4px 12px rgba(16, 185, 129, 0.35)'; this.style.background='linear-gradient(135deg, #059669 0%, #047857 100%)';" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 6px rgba(16, 185, 129, 0.25)'; this.style.background='linear-gradient(135deg, #10B981 0%, #059669 100%)';" title="Mencionado: ${userName}">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0; opacity: 0.95;">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
      <span style="line-height: 1.3; letter-spacing: 0.01em;">${userName}</span>
    </span>`;
    
    // Substituir a menção pelo HTML estilizado
    processedHtml = processedHtml.replace(fullMatch, mentionHtml);
  }
  
  return processedHtml;
}

/**
 * Limpa o cache de usuários (útil para forçar recarregamento)
 */
export function clearUsersCache(): void {
  usuariosCache.clear();
  usuariosCachePromise = null;
}

